/**
 * Full-width video block (Figma: full-width-image-video).
 * data-play-mode="autoplay" — muted background-style playback (MP4 / YouTube / Vimeo).
 * data-play-mode="manual" — poster + centered play; MP4 plays inline; embeds load on first click.
 *
 * data-video — source URL (same formats as videoPlayer.js).
 * data-poster — optional; MP4 poster frame; for manual embeds, preview image until play.
 */

import {
  VIDEO_TYPE,
  isDirectVideoFile,
  getVideoType,
  getYoutubeVideoId,
  getVimeoEmbedUrl,
  createMp4Video,
  createEmbedIframe,
  createVideoPlayer,
} from "./videoPlayer.js";

const LOADED_CLASS = "full-width-image-video--loaded";
const PLAYING_CLASS = "full-width-image-video--playing";
const GET_PARENT_SECTION = document.querySelectorAll(".full-width-media");

function markLoaded(section) {
  section.classList.add(LOADED_CLASS);
}

function syncSoundToggle(soundBtn, isMuted) {
  if (!soundBtn) return;
  soundBtn.setAttribute("data-muted", isMuted ? "true" : "false");
  soundBtn.setAttribute("aria-pressed", isMuted ? "true" : "false");
  soundBtn.setAttribute("aria-label", isMuted ? "Unmute video" : "Mute video");
}

/**
 * Lightweight controller wired to the sound toggle button.
 * Each player setup binds its own `setMuted` callback once the underlying
 * player/API is ready; clicks before binding silently no-op.
 */
function createSoundController(soundBtn, initialMuted = true) {
  let isMuted = initialMuted;
  let setter = null;

  syncSoundToggle(soundBtn, isMuted);

  soundBtn?.addEventListener("click", () => {
    const next = !isMuted;
    if (typeof setter === "function") {
      try {
        setter(next);
      } catch (_) {
        /* swallow player errors so the icon stays in sync */
      }
    }
    isMuted = next;
    syncSoundToggle(soundBtn, isMuted);
  });

  return {
    bind(newSetter, currentMuted) {
      setter = newSetter;
      if (typeof currentMuted === "boolean") {
        isMuted = currentMuted;
        syncSoundToggle(soundBtn, isMuted);
      }
    },
    update(nextMuted) {
      isMuted = !!nextMuted;
      syncSoundToggle(soundBtn, isMuted);
    },
    isMuted() {
      return isMuted;
    },
  };
}

/** YouTube IFrame postMessage helper for embeds that have `enablejsapi=1`. */
function ytPostCommand(iframe, func) {
  const win = iframe?.contentWindow;
  if (!win) return;
  try {
    win.postMessage(JSON.stringify({ event: "command", func, args: [] }), "*");
  } catch (_) {
    /* ignore */
  }
}

function setupPosterImg(section, poster, posterImg, { hideForMp4 } = {}) {
  if (!posterImg) return;
  if (!poster || hideForMp4) {
    posterImg.setAttribute("hidden", "");
    posterImg.removeAttribute("src");
    return;
  }
  posterImg.src = poster;
  posterImg.alt = "";
  posterImg.removeAttribute("hidden");
}

function setupManualMp4(section, url, poster, mediaHost, playBtn, posterImg, soundBtn) {
  // Overlay <img> for pause state — native <video poster> does not return after first play.
  setupPosterImg(section, poster, posterImg);

  const video = createMp4Video(url, () => markLoaded(section), {
    autoplay: false,
    loop: true,
    muted: true,
    interactive: true,
  });
  mediaHost.appendChild(video);

  const showPlayUi = () => {
    section.classList.remove(PLAYING_CLASS);
    playBtn?.removeAttribute("hidden");
  };

  const hidePlayUi = () => {
    section.classList.add(PLAYING_CLASS);
    playBtn?.setAttribute("hidden", "");
  };

  const sound = createSoundController(soundBtn, true);
  sound.bind((muted) => {
    video.muted = muted;
  }, video.muted);

  // Keep the toggle icon honest if the player itself flips mute (e.g. play button below).
  video.addEventListener("volumechange", () => sound.update(video.muted));

  playBtn?.addEventListener("click", () => {
    video.play().catch(() => {});
    hidePlayUi();
  });

  video.addEventListener("play", hidePlayUi);
  video.addEventListener("pause", showPlayUi);
  video.addEventListener("click", () => {
    if (!video.paused) {
      video.pause();
    }
  });
}

/** iOS Safari only allows unmuted playback from a direct user gesture; async onReady breaks that chain. Muted autoplay is allowed and starts the video; user unmutes in the YouTube bar. */
function isIosTouchDevice() {
  if (typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent || "";
  const isAppleTouch =
    /iPad|iPhone|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return isAppleTouch;
}

function ensureYoutubeIframeApiReady() {
  return new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve();
      return;
    }
    const queue = window.__fwivYtApiQueue || (window.__fwivYtApiQueue = []);
    queue.push(resolve);

    if (window.__fwivYtApiLoading) {
      return;
    }
    window.__fwivYtApiLoading = true;

    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof previous === "function") {
        previous();
      }
      const callbacks = window.__fwivYtApiQueue || [];
      window.__fwivYtApiQueue = [];
      callbacks.forEach((cb) => cb());
    };

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
  });
}

function syncYoutubeCustomPlayUi(section, playBtn, playerState) {
  const YT = window.YT;
  if (!YT?.PlayerState) {
    return;
  }

  const hideCustomPlay =
    playerState === YT.PlayerState.PLAYING || playerState === YT.PlayerState.BUFFERING;

  if (hideCustomPlay) {
    section.classList.add(PLAYING_CLASS);
    playBtn?.setAttribute("hidden", "");
  } else {
    section.classList.remove(PLAYING_CLASS);
    playBtn?.removeAttribute("hidden");
  }
}

function syncVimeoCustomPlayUi(section, playBtn, isPlaying) {
  if (isPlaying) {
    section.classList.add(PLAYING_CLASS);
    playBtn?.setAttribute("hidden", "");
  } else {
    section.classList.remove(PLAYING_CLASS);
    playBtn?.removeAttribute("hidden");
  }
}

function ensureVimeoPlayerApiReady() {
  return new Promise((resolve) => {
    if (window.Vimeo?.Player) {
      resolve();
      return;
    }
    const queue = window.__fwivVimeoApiQueue || (window.__fwivVimeoApiQueue = []);
    queue.push(resolve);

    if (window.__fwivVimeoApiLoading) {
      return;
    }
    window.__fwivVimeoApiLoading = true;

    const tag = document.createElement("script");
    tag.src = "https://player.vimeo.com/api/player.js";
    tag.onload = () => {
      const callbacks = window.__fwivVimeoApiQueue || [];
      window.__fwivVimeoApiQueue = [];
      callbacks.forEach((cb) => cb());
    };
    document.head.appendChild(tag);
  });
}

/**
 * YouTube manual: use `YT.Player` + `videoId` (official pattern). Wrapping a raw iframe
 * is brittle; the API injects the embed with correct `enablejsapi` / `playsinline` behavior.
 */
function setupManualYoutube(section, url, poster, mediaHost, playBtn, posterImg, soundBtn) {
  setupPosterImg(section, poster, posterImg, { hideForMp4: false });

  const videoId = getYoutubeVideoId(url);
  if (!videoId) {
    return;
  }

  let ytPlayer = null;
  const useIosMutedStart = isIosTouchDevice();
  const sound = createSoundController(soundBtn, true);
  sound.bind((muted) => {
    if (!ytPlayer) return;
    if (muted) ytPlayer.mute();
    else ytPlayer.unMute();
  }, true);

  const playerHostId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? `fwiv-yt-host-${crypto.randomUUID()}`
      : `fwiv-yt-host-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  playBtn?.addEventListener("click", () => {
    if (mediaHost.querySelector(".full-width-image-video__yt-host")) {
      if (ytPlayer) {
        if (useIosMutedStart && !sound.isMuted() && typeof ytPlayer.unMute === "function") {
          ytPlayer.unMute();
        }
        ytPlayer.playVideo();
      }
      return;
    }

    posterImg?.setAttribute("hidden", "");

    const host = document.createElement("div");
    host.className = "full-width-image-video__yt-host";
    host.id = playerHostId;
    mediaHost.appendChild(host);
    void mediaHost.offsetHeight;

    ensureYoutubeIframeApiReady().then(() => {
      requestAnimationFrame(() => {
        const rect = mediaHost.getBoundingClientRect();
        let w = Math.max(1, Math.floor(rect.width));
        let h = Math.max(1, Math.floor(rect.height));
        if (h < 2) {
          h = Math.max(180, Math.round((w * 9) / 16));
        }

        const create = () => {
          ytPlayer = new window.YT.Player(playerHostId, {
            videoId,
            width: w,
            height: h,
            playerVars: {
              // iOS: autoplay unmuted in onReady is blocked (not a "user gesture"); mute+play is allowed.
              autoplay: useIosMutedStart ? 0 : 1,
              controls: 0,
              playsinline: 1,
              modestbranding: 1,
              rel: 0,
              fs: 0,
              loop: 1,
              playlist: videoId,
              origin: window.location.origin,
              enablejsapi: 1,
            },
            events: {
              onReady: (event) => {
                markLoaded(section);
                const player = event.target;
                player.mute();
                player.playVideo();
                sound.update(true);
              },
              onStateChange: (event) => {
                syncYoutubeCustomPlayUi(section, playBtn, event.data);
              },
              onError: () => {
                section.classList.remove(PLAYING_CLASS);
                playBtn?.removeAttribute("hidden");
              },
            },
          });
        };

        if (window.YT?.ready) {
          window.YT.ready(create);
        } else {
          create();
        }
      });
    });
  });
}

/**
 * Vimeo manual: chromeless embed + Player.js so pause/ended restores the custom play icon.
 * Clicks inside a cross-origin iframe never bubble to the parent, so (unlike MP4) we cannot
 * detect taps on the video surface — and chromeless mode hides Vimeo's controls. A transparent
 * layer above the iframe while playing forwards taps to `player.pause()`.
 */
function setupManualVimeo(section, url, poster, mediaHost, playBtn, posterImg, soundBtn) {
  setupPosterImg(section, poster, posterImg, { hideForMp4: false });

  let vimeoPlayer = null;
  let pauseTapLayer = null;
  const useIosMutedStart = isIosTouchDevice();
  const sound = createSoundController(soundBtn, true);
  sound.bind((muted) => {
    if (!vimeoPlayer) return;
    vimeoPlayer.setMuted(muted).catch(() => {});
  }, true);

  const iframeId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? `fwiv-vm-${crypto.randomUUID()}`
      : `fwiv-vm-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  const setPauseTapLayerActive = (active) => {
    if (!pauseTapLayer) {
      return;
    }
    if (active) {
      pauseTapLayer.removeAttribute("hidden");
    } else {
      pauseTapLayer.setAttribute("hidden", "");
    }
  };

  playBtn?.addEventListener("click", () => {
    const existing = mediaHost.querySelector("iframe[data-fwiv-vimeo]");
    if (existing && vimeoPlayer) {
      if (
        useIosMutedStart &&
        !sound.isMuted() &&
        typeof vimeoPlayer.setMuted === "function"
      ) {
        vimeoPlayer.setMuted(false).catch(() => {});
      }
      vimeoPlayer.play().catch(() => {});
      return;
    }
    if (existing && !vimeoPlayer) {
      return;
    }

    const embedUrl = getVimeoEmbedUrl(url, {
      autoplay: false,
      muted: true,
      loop: true,
      background: false,
      controls: false,
    });

    if (!embedUrl) {
      return;
    }

    posterImg?.setAttribute("hidden", "");

    const iframe = createEmbedIframe(embedUrl, {
      interactive: true,
      revealDelayMs: 400,
      onReady: () => markLoaded(section),
      elementId: iframeId,
    });
    iframe.setAttribute("data-fwiv-vimeo", "");
    mediaHost.appendChild(iframe);

    if (!pauseTapLayer) {
      pauseTapLayer = document.createElement("div");
      pauseTapLayer.className = "full-width-image-video__pause-tap-layer";
      pauseTapLayer.setAttribute("hidden", "");
      pauseTapLayer.setAttribute("aria-hidden", "true");
      pauseTapLayer.addEventListener("click", (e) => {
        e.preventDefault();
        if (vimeoPlayer) {
          void vimeoPlayer.pause().catch(() => {});
        }
      });
      mediaHost.appendChild(pauseTapLayer);
    }

    ensureVimeoPlayerApiReady().then(() => {
      const PlayerCtor = window.Vimeo?.Player;
      if (!PlayerCtor) {
        section.classList.remove(PLAYING_CLASS);
        playBtn?.removeAttribute("hidden");
        return;
      }

      vimeoPlayer = new PlayerCtor(iframe);

      const onPlay = () => {
        syncVimeoCustomPlayUi(section, playBtn, true);
        setPauseTapLayerActive(true);
      };
      const onPauseOrEnd = () => {
        syncVimeoCustomPlayUi(section, playBtn, false);
        setPauseTapLayerActive(false);
      };

      vimeoPlayer.on("play", onPlay);
      vimeoPlayer.on("pause", onPauseOrEnd);
      vimeoPlayer.on("ended", onPauseOrEnd);
      vimeoPlayer.on("volumechange", (data) => {
        // `volume === 0` is the only reliable cross-browser mute signal from Vimeo's player.
        const muted = !data || data.volume === 0;
        sound.update(muted);
      });

      vimeoPlayer
        .setMuted(true)
        .then(() => vimeoPlayer.play())
        .catch(() => {});
      sound.update(true);
    });
  });
}

function setupManualEmbed(section, url, type, poster, mediaHost, playBtn, posterImg, soundBtn) {
  if (type === VIDEO_TYPE.YOUTUBE) {
    setupManualYoutube(section, url, poster, mediaHost, playBtn, posterImg, soundBtn);
    return;
  }

  if (type === VIDEO_TYPE.VIMEO) {
    setupManualVimeo(section, url, poster, mediaHost, playBtn, posterImg, soundBtn);
  }
}

function setupAutoplay(section, url, poster, mediaHost, playBtn, posterImg, soundBtn) {
  posterImg?.setAttribute("hidden", "");
  playBtn?.setAttribute("hidden", "");

  const { el, type } = createVideoPlayer(url, mediaHost, {
    onReady: () => markLoaded(section),
    // `enableJsApi` is required so we can postMessage mute/unMute commands to the YouTube iframe.
    youtube: { enableJsApi: true },
  });

  if (!el) {
    return;
  }

  if (isDirectVideoFile(type) && poster) {
    el.setAttribute("poster", poster);
  }

  const sound = createSoundController(soundBtn, true);

  if (isDirectVideoFile(type)) {
    sound.bind((muted) => {
      el.muted = muted;
    }, el.muted);
    el.addEventListener("volumechange", () => sound.update(el.muted));
    return;
  }

  if (type === VIDEO_TYPE.YOUTUBE) {
    sound.bind((muted) => ytPostCommand(el, muted ? "mute" : "unMute"), true);
    return;
  }

  if (type === VIDEO_TYPE.VIMEO) {
    ensureVimeoPlayerApiReady().then(() => {
      const PlayerCtor = window.Vimeo?.Player;
      if (!PlayerCtor) return;
      const player = new PlayerCtor(el);
      sound.bind((muted) => {
        player.setMuted(muted).catch(() => {});
      }, true);
      player.on("volumechange", (data) => {
        const muted = !data || data.volume === 0;
        sound.update(muted);
      });
    });
  }
}

export function initFullWidthImageVideo() {
  document.querySelectorAll(".full-width-image-video").forEach((section) => {
    const rawMode = (section.getAttribute("data-play-mode") || "manual").toLowerCase();
    const mode = rawMode === "autoplay" ? "autoplay" : "manual";
    const url = (section.getAttribute("data-video") || "").trim();
    const poster = (section.getAttribute("data-poster") || "").trim();
    const mediaHost = section.querySelector(".full-width-image-video__media");
    const playBtn = section.querySelector(".full-width-image-video__play");
    const posterImg = section.querySelector(".full-width-image-video__poster");
    const soundBtn = section.querySelector(".full-width-image-video__sound");
    section.closest(".fl-module")?.classList.add("full-width-media--video__fixed");
    section.closest(".fl-col-group")?.classList.add("full-width-media--video__fixed");
    section.closest(".fl-row ")?.classList.add("full-width-media--video__fixed");

    if (!url || !mediaHost) {
      return;
    }

    if (mode === "autoplay") {
      setupAutoplay(section, url, poster, mediaHost, playBtn, posterImg, soundBtn);
      return;
    }

    const type = getVideoType(url);
    if (!type) {
      return;
    }

    if (isDirectVideoFile(type)) {
      setupManualMp4(section, url, poster, mediaHost, playBtn, posterImg, soundBtn);
    } else {
      setupManualEmbed(section, url, type, poster, mediaHost, playBtn, posterImg, soundBtn);
    }
  });
}
